import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import './quotes.css';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../../components/AppToolbar';
import { useConfirm } from '../../../components/confirm';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { downloadQuoteReport, reactivateQuotes, reportErrorMessage } from './detailApi';

// ─────────────────────────────────────────────────────────────────────────────
// CRM ▸ Accounts ▸ Quotes ▸ Expired Quotes (nav `crm-quotes-expired`).
// Port of legacy ALPACA `CRMQuotes_ListView_Expired` (BTLIMS.Web/Model.xafml
// :13988). Same 8 columns as Canceled Quotes, with the same two form-specific
// toolbar actions in the legacy order:
//
//   Preview · Reactive · Refresh · Export to
//
// The list DATA is the shared ModuleRegistry entry (`GET /api/v1/modules/
// crm-quotes-expired`); this page adds ONLY the two actions, touching no other
// quotes form (owner request 2026-10-29).
//
//   Preview  — legacy `btnqrpreview`: exactly ONE selected quote → its PDF report.
//   Reactive — legacy `QuoteReactive_Execute` :7130 → :4710. REFUSES any selected
//              quote still past its expiration date ("Please update expiredate.",
//              legacy :7135 / Model.xafml :1236); otherwise asks for a reason and
//              resets Status → Pending Submission (the expired quote is NOT
//              cancelled, so the cancellation fields are left untouched — the one
//              difference from the Canceled list). `POST /quotes/reactivate` with
//              `fromExpired: true`.
//
// AUDIT. Reactivate writes under the "Open Quotes" FormName.
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_ID = 'crm-quotes-expired';

// `toLocaleString` on a NUMBER is digit grouping, not a date (C2).
const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** One row as the ModuleRegistry `crm-quotes-expired` entry returns it. */
interface ExpiredQuoteRow {
  oid: string;
  date: string | null;
  quoteId: string | null;
  title: string | null;
  client: string | null;
  prospect: boolean;
  amount: number | null;
  expirationDate: string | null;
  status: string | null;
}

// Legacy CRMQuotes_ListView_Expired visible columns (model Index 0-7).
const COLUMNS: AppColumn<ExpiredQuoteRow>[] = [
  { field: 'date', header: 'Date', width: 120, type: 'date' },
  { field: 'quoteId', header: 'QuoteID', width: 130 },
  { field: 'title', header: 'Title', width: 200 },
  { field: 'client', header: 'Client', width: 180 },
  {
    field: 'prospect', header: 'Prospect', width: 90, align: 'center',
    render: (r) => <input type="checkbox" checked={!!r.prospect} readOnly disabled aria-label="Prospect" />,
  },
  {
    field: 'amount', header: 'Amount', width: 120, align: 'right', type: 'number',
    render: (r) => (r.amount == null ? '' : CURRENCY.format(r.amount)),
  },
  { field: 'expirationDate', header: 'ExpirationDate', width: 140, type: 'date' },
  { field: 'status', header: 'Status', width: 130, type: 'status' },
];

export default function ExpiredQuotesPage({ onHome }: { onHome?: () => void }) {
  const notify = useNotify();
  const confirm = useConfirm();
  const [rows, setRows] = useState<ExpiredQuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);
  // Grid maximize is driven from the search-row button (controlled, below).
  const [maxed, setMaxed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ data?: ExpiredQuoteRow[]; rows?: ExpiredQuoteRow[] }>(
        `/api/v1/modules/${PAGE_ID}`, { params: { pageSize: 1000 } });
      const data = res.data?.data ?? res.data?.rows ?? [];
      setRows(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load expired quotes.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.quoteId, r.title, r.client, r.status].some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  /** Preview — legacy `btnqrpreview`, exactly ONE selected quote. */
  async function preview() {
    if (selected.size !== 1) {
      notify.warning('Please select an item to proceed further.');
      return;
    }
    try {
      const filename = await downloadQuoteReport(String([...selected][0]));
      notify.success(`${filename} downloaded.`);
    } catch (e) {
      notify.error((await reportErrorMessage(e)) ?? 'Failed to generate the quote report.');
    }
  }

  /** Reactive — legacy `QuoteReactive_Execute` :7130 → `Dcreason_Accepting` :4710.
   *  A quote still past its expiration date must be extended first. */
  async function reactive() {
    const ids = [...selected].map(String);
    if (ids.length === 0) {
      notify.warning('Please select an item to proceed further.');
      return;
    }
    // Legacy isexpireupdate (:7132): if ANY selected quote's expiration date is
    // still before today, refuse before asking for a reason. The server enforces
    // this too; this only saves the user from typing a reason in vain.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const stillExpired = rows
      .filter((r) => selected.has(r.oid))
      .some((r) => {
        if (!r.expirationDate) return false;
        const d = new Date(r.expirationDate);
        d.setHours(0, 0, 0, 0);
        return d.getTime() < today.getTime();
      });
    if (stillExpired) {
      notify.warning('Please update expiredate.');
      return;
    }
    const reason = await confirm.prompt({
      title: 'Reactive Reason',
      message: ids.length === 1
        ? 'Enter a reason to reactivate this quote:'
        : `Enter a reason to reactivate ${ids.length} quotes:`,
      placeholder: 'Reason…',
      required: true,
    });
    if (!reason) return;
    setBusy(true);
    try {
      const r = await reactivateQuotes(ids, reason, true);
      notify.success(r.reactivated === 1
        ? 'Quote reactivated successfully.'
        : `${r.reactivated} quotes reactivated successfully.`);
      await load();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to reactivate.');
    } finally {
      setBusy(false);
    }
  }

  // Preview / Refresh / Export auto-register through <AppToolbar>; Reactive is a
  // form-specific button, so it registers its own hotkey here (standing rule 14).
  useHotkeys([
    {
      combo: 'alt+k', label: 'Reactive', group: 'Expired Quotes', order: 1,
      disabled: selected.size === 0 || busy, run: () => void reactive(),
    },
  ]);

  // While maximized, Esc restores (matches the search-row Restore button). The
  // page-level maximize (page-fullgrid) has no grid overlay to own the key.
  useEffect(() => {
    if (!maxed) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaxed(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maxed]);

  return (
    <div className={`bl-page mlv-host cq-expired-page${maxed ? ' page-fullgrid' : ''}`}>
      <div className="bl-grid-area">
        {/* Chrome (breadcrumb · title · toolbar) is HIDDEN while maximized, so the
            search row + grid fill the content area (page-fullgrid) — the nav stays
            and the search row keeps its Export + Restore controls, like Open
            Prospects (owner request 2026-10-29). */}
        {!maxed && (<>
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>CRM</span>
          <i className="ti ti-chevron-right" />
          <span>Accounts</span>
          <i className="ti ti-chevron-right" />
          <span>Quotes</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Expired Quotes</span>
        </div>
        <FormTitle pageId={PAGE_ID} loadMs={loadMs} loading={loading} auditFormName="Open Quotes" />
        <AppToolbar
          // Legacy order: Preview · Reactive · Refresh · Export to.
          onPreview={() => void preview()}
          previewDisabled={selected.size !== 1}
          beforeRefresh={
            <button type="button" className="ca-btn" onClick={() => void reactive()}
                    disabled={selected.size === 0 || busy} title="Reactive (Alt+K)">
              <span className="tb-i tb-i-green">♻</span> Reactive
            </button>
          }
          onRefresh={() => void load()}
          refreshing={loading}
          exportData={{ filename: 'expired-quotes', ...gridExportMatrix(shown, COLUMNS) }}
        />
        </>)}
        {/* Search on the LEFT, Export + Maximize on the RIGHT of the SAME row,
            OUTSIDE the grid card — the ModuleListView list look (owner request
            2026-10-29, image 1074). The grid's own header tools are hidden
            (hideHeaderTools) and its maximize is driven from the button here. */}
        <GridSearch
          value={search}
          onChange={setSearch}
          placeholder="Text to search..."
          right={
            <div className="mlv-grid-acts">
              <ExportMenu iconOnly triggerClassName="dv-gridact"
                          filename="expired-quotes" {...gridExportMatrix(shown, COLUMNS)} />
              <button type="button" className={`dv-gridact${maxed ? ' dv-on' : ''}`}
                      onClick={() => setMaxed((v) => !v)}
                      title={maxed ? 'Restore' : 'Maximize'}
                      aria-label={maxed ? 'Restore grid' : 'Maximize grid'}>
                <i className={`ti ti-arrows-${maxed ? 'minimize' : 'maximize'}`} aria-hidden="true" />
              </button>
            </div>
          }
        />
        <AppDataGrid<ExpiredQuoteRow>
          rows={shown}
          columns={COLUMNS}
          getRowId={(r) => r.oid}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          hideHeaderTools
          defaultPageSize={20}
          emptyText={search ? 'No results match your search.' : 'No data to display'}
        />
      </div>
    </div>
  );
}
